import { debounce } from "lodash"
import { Fragment, useState } from "react"
import { BaseTable, Button, Card, Checkbox, HeaderMenu, Input, Loading, PaginationData, Select, TableNumber } from "components"
import { convertNumber, default_pagination_value, useApi, useLocation, useMultipleSelect } from "utils"
import { useProductCategory } from "pages/Inventori/Transaction/AddNewItem/components/FormSection/utils"
import { PicturesSection } from "pages/Inventori/Register/ItemGroup/components"
import { useAllId } from "pages/Inventori/Register/ItemGroup/utils"
import { useListStockAlert } from "./hooks"
import { StockAlertParams } from "./types"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import { PERMISION } from "security"

export const StockAlertListPage = () => {
  const [pagination, setPagination] = useState<StockAlertParams>({
    ...default_pagination_value,
    zero_soh:true
  })
  
  const api = useApi()
  const navigate = useNavigate();
  const multiple = useMultipleSelect();
  const getAllId = useAllId();
  const getCategory = useProductCategory();
  const getLocation = useLocation()
  const getStockAlert = useListStockAlert(pagination);

  const onDebounce = debounce(e => setPagination(prev => { return { ...prev, keyword: e.target.value } }))
  const selectedItems = getStockAlert?.data?.items?.filter(item => multiple?.item?.includes(String(item.product_id)));

  const isDisableActions = Boolean(pagination?.location_id === pagination?.source_location_id)
  const onLoading = <tr><td colSpan={13} className="text-center"><Loading loading /></td></tr>
  const onEmpty = <tr><td colSpan={14} className="text-center"><Loading errorText="No data available" /></td></tr>

  const calculateDiv = (soh: number, min: number): number => {
    if (soh <= min) {
      return Math.min(soh - min, 0);
    }
    return 0;
  };

  return (
    <section className="container my-5">
      <HeaderMenu title="STOCK ALERT" className="sticky top-[45px] mb-3 z-[10]" />

      <Card className="mb-3">
        <Card.Body>
          <div className="lg:flex lg:justify-between items-center">
            <div className="lg:flex items-end gap-3">
              <Input
                label="SEARCH"
                className="lg:w-[300px] md:w-full"
                placeholder="Search data..."
                onChange={onDebounce}
              />
            </div>

            <div className="lg:flex lg:items-end gap-2">
              <Select
                label="LOCATION"
                placeholder="Select location"
                className="lg:w-[200px] w-full me-2"
                width={true}
                isLoading={getLocation.isLoading}
                options={getLocation?.data}
                value={getLocation?.data?.find((item) => item.value === pagination?.location_id)}
                onChange={(e) => setPagination({ ...pagination, location_id: e?.value ?? "", page: 1 })}
              />
              <Select
                label="CATEGORY"
                placeholder="Select category"
                className="lg:w-[200px] w-full me-2"
                width={true}
                defaultValue={{ value: "0", label: "All" }}
                options={[{ label: "All", value: "" }].concat(getCategory?.dataCategory)}
                isLoading={getCategory.isLoadingCategory}
                value={getCategory?.dataCategory.find((item) => item.value === pagination?.category_id)}
                onChange={(e) => setPagination({ ...pagination, category_id: e?.value ?? "", page: 1 })}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 lg:mb-3 mt-3">
              <Checkbox
                defaultChecked={pagination?.buy}
                onChange={(e) => setPagination({ ...pagination, buy: e.target.checked ?? "", page: 1 })}
              >
                BUY
              </Checkbox>
              <Checkbox
                defaultChecked={pagination?.sell}
                onChange={(e) => setPagination({ ...pagination, sell: e.target.checked ?? "", page: 1 })}
              >
                SELL
              </Checkbox>
              <Checkbox
                defaultChecked={pagination?.inv}
                onChange={(e) => setPagination({ ...pagination, inv: e.target.checked ?? "", page: 1 })}
              >
                INVENTORY
              </Checkbox>
              <Checkbox
                defaultChecked={pagination?.build}
                onChange={(e) => setPagination({ ...pagination, build: e.target.checked ?? "", page: 1 })}
              >
                <span className="text-nowrap">BUILD ITEM</span>
              </Checkbox>
            </div>

            <div className="flex items-center gap-3 lg:mb-3 mt-3">
              <Checkbox
                defaultChecked={pagination?.zero_soh}
                onChange={(e) => setPagination({ ...pagination, zero_soh: e.target.checked ?? "", page: 1 })}
              >
                ZERO SOH
              </Checkbox>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <div className="lg:flex lg:justify-between items-center mt-3 ml-4 mr-3">
          <Select
            label="SOURCE LOCATION"
            placeholder="Select source location"
            className="lg:w-[250px] w-full me-2"
            width={true}
            isLoading={getLocation.isLoading}
            options={getLocation?.data}
            value={getLocation?.data?.find((item) => item.value === pagination?.source_location_id)}
            onChange={(e) => setPagination({ ...pagination, source_location_id: e?.value ?? "", page: 1 })}
          />

          <Input
            label="DESTINATION"
            placeholder="-"
            className="lg:w-[250px] w-full me-2"
            value={getLocation?.data?.find((item) => item.value === pagination?.location_id)?.label}
            disabled
          />
        </div>
        <Card.Body>
          <BaseTable>
            <thead className="sticky top-[200px] z-[3] !bg-black !text-white">
              <tr>
                <th>NO</th>
                <th>CATEGORY</th>
                <th>PICTURES</th>
                <th>BSI</th>
                <th>BUILD</th>
                <th>BARCODE</th>
                <th>PRODUCT</th>
                <th>
                  <span>UNIT</span><br />
                  <span>(inventory)</span>
                </th>
                <th>SOH</th>
                <th>SOH SOURCE</th>
                <th>MIN</th>
                <th>MAX</th>
                <th>DIV</th>
                <th>
                  {getAllId.isLoading ? <Loading loading /> : (
                    <Checkbox
                      value={Boolean(multiple?.item?.length === getAllId?.data?.length)}
                      onClick={() => multiple.handleData(Boolean(multiple?.item?.length === getAllId?.data?.length) ? [] : getAllId?.data)}
                      disabled={isDisableActions}
                    />
                  )}
                </th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {getStockAlert.isLoading ? onLoading : getStockAlert?.data?.items?.length === 0 ? onEmpty : (
                <Fragment>
                  {getStockAlert?.data?.items?.map((item, key) => (
                    <tr>
                      <td className="text-center">
                        {TableNumber({
                          dataLength: getStockAlert?.data?.pagination_data?.size,
                          pageCurrent: pagination?.page!,
                          index: key,
                          limit: pagination?.limit!,
                          totalData: pagination?.total!,
                        })}
                      </td>
                      <td>{item?.mr_product_category_name ?? "-"}</td>
                      <td className="!w-[80px]">
                        <div className="flex justify-center">
                          {Boolean(item?.pictures?.length) && (
                            <PicturesSection
                              dataPicture={item?.pictures}
                              items={1}
                              className="size-[50px] my-[-10px]"
                            />
                          )}
                        </div>
                      </td>
                      <td className="text-center">{`${item?.buy ? "B" : ""}${item?.sell ? "S" : ""}${item?.inventory ? "I" : "-"}`}</td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary"
                          checked={item?.build_item}
                          disabled
                        />
                      </td>
                      <td>{item?.product_barcode ?? "-"}</td>
                      <td>{item?.product_name ?? "-"}</td>
                      <td>{item?.unit_convention_name ?? "-"}</td>
                      <td>{convertNumber(parseFloat((item?.soh || 0)?.toString()))?.intoCurrency}</td>
                      <td>{convertNumber(parseFloat((item?.soh_source || 0)?.toString()))?.intoCurrency}</td>
                      <td>{convertNumber(parseFloat((item?.min_qty || 0)?.toString()))?.intoCurrency}</td>
                      <td>{convertNumber(parseFloat((item?.max_qty || 0)?.toString()))?.intoCurrency}</td>
                      <td className="text-center text-error">{convertNumber(calculateDiv(Number(item?.soh ?? 0), Number(item?.min_qty ?? 0)))?.intoCurrency}</td>
                      <td style={{ width: 50 }}>
                        <Checkbox
                          value={Boolean(multiple?.item?.find((i) => i === item?.product_id?.toString()))}
                          onClick={() => multiple?.handleData(item?.product_id?.toString()!)}
                          disabled={isDisableActions}
                        />
                      </td>
                      <td style={{ width: 50 }}>
                        <div className="flex items-center justify-center my-[-10px] mx-[-11px]">
                          <Button 
                            permission={PERMISION.INVENTORY.REPORT.STOCK_ALERT.UPDATE}
                            color="primary" 
                            disabled={!Boolean(pagination?.source_location_id) || isDisableActions}
                            onClick={() => {
                              const bsi = item?.buy && item?.sell && item?.inventory
                              const payload = [{ product_id: item?.product_id, ship_qty: bsi ? Number(item?.unit_convention ?? 0) : Number(item?.max_qty ?? 0) - Number(item?.soh ?? 0) }]

                              api.post(`/transfer/create-product-transfer?source_id=${pagination?.source_location_id}&destination_id=${pagination?.location_id}`, { request_data: payload })
                              .then((res) => navigate("/inventory/transaction/transfer-items", { state: { 
                                data: res?.data?.payload?.products, 
                                sourceLocation: pagination?.source_location_id ,
                                location: pagination?.location_id
                              }}))
                              .catch(err => toast.error(err?.response?.data?.message))
                            }}
                          >
                            TRANSFER
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              )}
            </tbody>
          </BaseTable>
          <div className="flex justify-end mr-[5px]">
            <Button 
              permission={PERMISION.INVENTORY.REPORT.STOCK_ALERT.UPDATE}
              color="primary" 
              disabled={!pagination?.source_location_id || multiple?.item?.length === 0 || isDisableActions}
              onClick={() => {
                const payload = selectedItems.map((item: any) => {
                  const bsi = item?.buy && item?.sell && item?.inventory;

                  return {
                    product_id: item?.product_id,
                    ship_qty: bsi ? Number(item?.unit_convention ?? 0) : Number(item?.max_qty ?? 0) - Number(item?.soh ?? 0)
                  };
                });

                api.post(`/transfer/create-product-transfer?source_id=${pagination?.source_location_id}&destination_id=${pagination?.location_id}`, { request_data: payload })
                .then((res) => navigate("/inventory/transaction/transfer-items", { state: { 
                  data: res?.data?.payload?.products, 
                  sourceLocation: pagination?.source_location_id ,
                  location: pagination?.location_id
                }}))
                .catch(err => toast.error(err?.response?.data?.message))
              }}
            >
              TRANSFER
            </Button>
          </div>

          <div className="mt-5">
            <PaginationData data={getStockAlert?.data} pagination={pagination} setPagination={setPagination} />
          </div>
        </Card.Body>
      </Card>
    </section>
  )
}